import React from "react";
import { Link, useLocation, Outlet } from "react-router-dom";
import { motion } from "framer-motion";
import { LogOut } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { adminMenuItems } from "../utils/adminMenuItems";

interface AdminLayoutProps {
  children?: React.ReactNode;
}

const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const location = useLocation();
  const { logout } = useAuth();

  const handleLogout = () => {
    logout();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-mango-50 via-farm-50 to-golden-50">
      <div className="flex pt-[66px]" style={{ overflowX: "hidden" }}>
        {/* Left Sidebar - Fixed - Hidden on mobile */}
        <aside className="hidden md:flex fixed left-0 top-[66px] w-48 h-[calc(100vh-66px)] bg-white/95 backdrop-blur-sm border-r border-gray-200/80 shadow-sm z-40 flex-col">
          {/* Navigation Menu */}
          <nav className="flex-1 p-2 space-y-1 overflow-y-auto">
            {adminMenuItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`group relative flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-[color,background-color,box-shadow] duration-200 active:bg-mango-100/80 ${
                    isActive
                      ? "bg-gradient-to-r from-mango-100 to-mango-50 text-mango-700 shadow-sm ring-1 ring-inset ring-mango-200/70"
                      : "text-gray-700 hover:bg-mango-50/70 hover:text-mango-700"
                  }`}
                  aria-current={isActive ? "page" : undefined}
                >
                  {isActive && (
                    <motion.span
                      layoutId="admin-active-bar"
                      className="absolute left-0 top-2 bottom-2 w-1 rounded-full bg-gradient-to-b from-mango-500 to-golden-500"
                      transition={{ type: "spring", stiffness: 500, damping: 34 }}
                    />
                  )}
                  <Icon className="h-4 w-4 flex-shrink-0 transition-transform duration-300 group-hover:scale-110" />
                  <span>{item.name}</span>
                </Link>
              );
            })}
          </nav>

          {/* Logout Button */}
          <div className="p-2 border-t border-gray-200 flex-shrink-0">
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-red-600 hover:bg-red-50 hover:text-red-700 active:scale-[0.98] transition duration-200"
              aria-label="Logout from admin panel"
            >
              <LogOut className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
              <span>Logout</span>
            </button>
          </div>
        </aside>

        {/* Main Content Area */}
        <main className="flex-1 md:ml-48 overflow-y-auto min-h-[calc(100vh-66px)]" style={{ overflowX: "hidden" }}>
          {children || <Outlet />}
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;

